import React, { useState, useEffect } from "react";
import axios from "axios";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import {
  Grid,
  TextField,
  Box,
  Select,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  Autocomplete,
  IconButton,
} from "@mui/material";
import {
  StyledTypography,
  StyledButton,
  StyledAlert,
  StyledSnackbar,
  StyledAgGridContainer,
  StyledTableContainer,
} from "components/StyledComponents/styledComponents.js";
import { useMaterialUIController } from "context";
import URLs from "constants/urls";
import Cookie from "js-cookie";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ResponsiveTabs from "components/StyledComponents/styledComponents";

export default function EmployeeMasterHrms() {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [applicants, setApplicants] = useState([]);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [employees, setEmployees] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);

  const tabs = [{ label: "Employee List" }, { label: "Employee Registration" }];
  const [formData, setFormData] = useState({
    applicantId: 0,
    companyId: 1,
    firstName: "",
    lastName: "",
    dateOfJoining: "",
    dateOfBirth: "",
    doorNo: "",
    streetName: "",
    cityName: "",
    districtName: "",
    stateName: "",
    zipCode: "",
    phoneNumber: "",
    alternatePhoneNumber: "",
    emailId: "",
    reference: "",
    role: null,
    roleId: 0,
    ctcPerYear: 0,
    additionalComments: "",
    resumeAttachment: "",
    basic: 0,
    password: "",
    applicationAccess: "No",
    photo: null,
    photoUrl: "",
    photoAttachmentPresignedUrl: "",
    photoAttachment: "",
    assignedToRoleId: null,
    assignedToRoleName: "",
    assignedEmployeeId: null,
    assignedEmployeeName: "",
  });

  const [assignedRoles, setAssignedRoles] = useState([]);
  const [assignedEmployees, setAssignedEmployees] = useState([]);
  const [selectedAssignedRole, setSelectedAssignedRole] = useState(null);
  const [selectedAssignedEmployee, setSelectedAssignedEmployee] = useState(null);
  const [controller] = useMaterialUIController();
  const { user } = controller;
  const hrms_token = Cookie.get("hrms_token");

  const setAlert = (msg, type) => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (activeTab === 0) {
      fetchEmployees();
      setIsEditMode(false);
      setSelectedEmployeeId(null);
      resetForm();
    } else if (activeTab === 1) {
      fetchApplicantData();
    }
  }, [activeTab]);

  const fetchAssignedRoles = async (roleId) => {
    if (!roleId) return;
    setLoading(true);
    try {
      const response = await fetch(`${URLs.assignedRoleDetails}/${roleId}`, {
        headers: {
          Authorization: `Bearer ${hrms_token}`,
        },
      });
      if (!response.ok) throw new Error(`Failed to fetch assigned roles: ${response.status}`);
      const data = await response.json();
      setAssignedRoles(data);
    } catch (err) {
      setAlert(err.message, "error");
      setAssignedRoles([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignedEmployees = async (roleId) => {
    if (!roleId) return;
    setLoading(true);
    try {
      const response = await fetch(`${URLs.getemplyeenamesby}/${roleId}`, {
        headers: {
          Authorization: `Bearer ${hrms_token}`,
        },
      });
      if (!response.ok) throw new Error(`Failed to fetch assigned employees: ${response.status}`);
      const data = await response.json();
      setAssignedEmployees(data);
    } catch (err) {
      setAlert(err.message, "error");
      setAssignedEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchApplicantData = async () => {
    const url = URLs.hrmsGetNotEmployees;
    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${hrms_token}`,
      },
    };
    setLoading(true);
    try {
      const response = await fetch(url, options);
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      const data = await response.json();
      setApplicants(data);
    } catch (error) {
      setAlert(`Failed to fetch applicant data: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const response = await fetch(URLs.hrmsGetEmployeeDetailsUrl, {
        headers: {
          Authorization: `Bearer ${hrms_token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      const data = await response.json();
      setEmployees(data);
    } catch (error) {
      setAlert(`Failed to fetch employees: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleApplicantChange = (event, newValue) => {
    setSelectedApplicant(newValue);
    if (newValue) {
      const formatDate = (dateArr) => {
        if (!dateArr || !Array.isArray(dateArr)) return "";
        const [year, month, day] = dateArr;
        return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      };

      setFormData({
        applicantId: newValue.applicantId || 0,
        companyId: newValue.companyId || 1,
        firstName: newValue.firstName || "",
        lastName: newValue.lastName || "",
        dateOfJoining: formatDate(newValue.dateOfJoining) || "",
        dateOfBirth: formatDate(newValue.dateOfBirth) || "",
        doorNo: newValue.doorNo || "",
        streetName: newValue.empAddress?.streetName || "",
        cityName: newValue.empAddress?.cityName || "",
        districtName: newValue.empAddress?.districtName || "",
        stateName: newValue.empAddress?.stateName || "",
        zipCode: newValue.empAddress?.zipCode || "",
        phoneNumber: newValue.phoneNumber || "",
        alternatePhoneNumber: newValue.alternatePhoneNumber || "",
        emailId: newValue.emailId || "",
        reference: newValue.reference || "",
        role: newValue.role?.roleName || "",
        roleId: newValue.role?.roleId || 0,
        ctcPerYear: newValue.ctcPerYear || 0,
        additionalComments: newValue.additionalComments || "",
        resumeAttachment: newValue.resumeAttachment || "",
        basic: newValue.basic || 0,
        password: "",
        applicationAccess: "No",
        photo: null,
        photoUrl: newValue.photoAttachmentPresignedUrl || "",
        photoAttachmentPresignedUrl: newValue.photoAttachmentPresignedUrl || "",
        photoAttachment: newValue.photoAttachment || "",
      });
      if (newValue.role?.roleId) {
        fetchAssignedRoles(newValue.role.roleId);
      } else {
        setAssignedRoles([]);
      }
    } else {
      resetForm();
      setAssignedRoles([]);
    }
  };

  const handleInputChange = (field) => (event, value) => {
    if (field === "assignedToRoleId") {
      setSelectedAssignedRole(value);
      setFormData({
        ...formData,
        assignedToRoleId: value ? value.roleId : null,
        assignedToRoleName: value ? value.roleName : "",
        assignedEmployeeId: null,
        assignedEmployeeName: "",
      });
      setSelectedAssignedEmployee(null);
      setAssignedEmployees([]);
      if (value && value.roleId) fetchAssignedEmployees(value.roleId);
      return;
    }
    if (field === "assignedEmployeeId") {
      setSelectedAssignedEmployee(value);
      setFormData({
        ...formData,
        assignedEmployeeId: value ? value.employeeId : null,
        assignedEmployeeName: value ? value.employeeName : "",
      });
      return;
    }
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setAlert("File size should be less than 5MB", "error");
      event.target.value = "";
      return;
    }

    const allowedTypes = ["image/jpeg", "image/png", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      setAlert("Please upload an image file (JPEG, PNG, or GIF)", "error");
      event.target.value = "";
      return;
    }

    if (!formData.phoneNumber) {
      setAlert("Please enter a phone number before uploading the photo", "error");
      event.target.value = "";
      return;
    }

    setLoading(true);

    try {
      const uniqueFileName = `${formData.phoneNumber}_${file.name}`;
      const apiUrl = `https://s3service.sconexsoft.com/s3bucket/presigned-url?fileName=${encodeURIComponent(
        uniqueFileName
      )}&bucketName=sconexsofthrms&operation=upload`;
      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      const url = response.data;
      if (!url || typeof url !== "string" || !url.startsWith("https://")) {
        throw new Error("Invalid pre-signed URL");
      }

      await axios.put(url, file, {
        headers: {
          "Content-Type": file.type,
        },
      });

      setFormData((prev) => ({
        ...prev,
        photo: file,
        photoUrl: url.split("?")[0],
        photoAttachmentPresignedUrl: url.split("?")[0],
        photoAttachment: uniqueFileName,
      }));

      setAlert("Photo uploaded successfully!", "success");
    } catch (err) {
      console.error("Upload Error:", err);
      setAlert(`Failed to upload photo: ${err.message || "Unknown error"}`, "error");
    } finally {
      setLoading(false);
      event.target.value = "";
    }
  };

  const handleEditEmployee = (employee) => {
    setIsEditMode(true);
    setSelectedEmployeeId(employee.employeeId);
    setActiveTab(1);

    const formatDateString = (dateStr) => {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      return date.toISOString().split("T")[0];
    };

    setFormData({
      applicantId: employee.applicantId || 0,
      companyId: employee.companyId || 1,
      firstName: employee.firstName || "",
      lastName: employee.lastName || "",
      dateOfJoining: formatDateString(employee.dateOfJoining) || "",
      dateOfBirth: formatDateString(employee.dateOfBirth) || "",
      employeeCode: employee.employeeCode || "",
      doorNo: employee.address?.doorNo || "",
      streetName: employee.address?.streetName || "",
      cityName: employee.address?.cityName || "",
      districtName: employee.address?.districtName || "",
      stateName: employee.address?.stateName || "",
      zipCode: employee.address?.zipCode || "",
      phoneNumber: employee.phoneNumber || "",
      alternatePhoneNumber: employee.alternatePhoneNumber || "",
      emailId: employee.emailId || "",
      reference: employee.reference || "",
      role: employee.role?.roleName || "",
      roleId: employee.role?.roleId || 0,
      ctcPerYear: employee.ctcPerYear || 0,
      password: "",
      applicationAccess: employee.applicationAccess === "Y" ? "Yes" : "No",
      photo: null,
      photoUrl: employee.photoAttachmentPresignedUrl || "",
      photoAttachmentPresignedUrl: employee.photoAttachmentPresignedUrl || "",
      photoAttachment: employee.photoAttachment || "",
    });
  };

  const handleUpdateEmployee = async () => {
    if (!selectedEmployeeId) {
      setAlert("No employee selected for update", "error");
      return;
    }

    if (!formData.emailId) {
      setAlert("Email is required", "error");
      return;
    }
    if (!formData.dateOfBirth) {
      setAlert("Date of Birth is required", "error");
      return;
    }
    if (!formData.dateOfJoining) {
      setAlert("Date of Joining is required", "error");
      return;
    }

    const payload = {
      employeeId: selectedEmployeeId,
      employeeCode: formData.employeeCode || selectedEmployeeId?.toString() || "",
      dateOfJoining: formData.dateOfJoining,
      dateOfBirth: formData.dateOfBirth,
      address: {
        doorNo: formData.doorNo,
        streetName: formData.streetName,
        cityName: formData.cityName,
        districtName: formData.districtName,
        stateName: formData.stateName,
        zipCode: formData.zipCode,
      },
      phoneNumber: formData.phoneNumber,
      emailId: formData.emailId,
      alternatePhoneNumber: formData.alternatePhoneNumber,
      reference: formData.reference,
      role: {
        roleId: formData.roleId,
        roleName: formData.role,
      },
      ctcPerYear: formData.ctcPerYear,
      applicationAccess: formData.applicationAccess === "Yes" ? "Y" : "N",
      companyId: user.dsaId,
      activeStatus: "Y",
      payRollStatus: "N",
      applicantId: formData.applicantId,
      photoAttachment: formData.photoAttachment || "",
      password: formData.password || undefined,
      lastName: formData.lastName,
      firstName: formData.firstName,
      assignedTo: formData.assignedToRoleId || 0,
      assignedEmployee: formData.assignedEmployeeId || 0,
    };

    setLoading(true);
    try {
      const response = await fetch(`${URLs.hrmsGetEmployeeDetailsUrl}/${selectedEmployeeId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Update failed: ${response.status}`);
      }

      setAlert("Employee updated successfully", "success");
      setIsEditMode(false);
      setSelectedEmployeeId(null);
      resetForm();
      setActiveTab(0);
      fetchEmployees();
    } catch (error) {
      console.error("Update error:", error);
      setAlert(`Update failed: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!selectedApplicant) {
      setAlert("Please select an applicant", "error");
      return;
    }
    if (!formData.password) {
      setAlert("Please enter a password", "error");
      return;
    }
    if (!formData.emailId) {
      setAlert("Email is required", "error");
      return;
    }
    if (!formData.dateOfBirth) {
      setAlert("Date of Birth is required", "error");
      return;
    }
    if (!formData.dateOfJoining) {
      setAlert("Date of Joining is required", "error");
      return;
    }

    const payload = {
      employeeId: 0,
      employeeCode: "",
      dateOfJoining: formData.dateOfJoining,
      dateOfBirth: formData.dateOfBirth,
      address: {
        doorNo: formData.doorNo,
        streetName: formData.streetName,
        cityName: formData.cityName,
        districtName: formData.districtName,
        stateName: formData.stateName,
        zipCode: formData.zipCode,
      },
      phoneNumber: formData.phoneNumber,
      emailId: formData.emailId,
      alternatePhoneNumber: formData.alternatePhoneNumber,
      reference: formData.reference,
      role: {
        roleId: formData.roleId,
        roleName: formData.role,
      },
      ctcPerYear: formData.ctcPerYear,
      applicationAccess: formData.applicationAccess === "Yes" ? "Y" : "N",
      companyId: user.dsaId,
      activeStatus: "Y",
      payRollStatus: "N",
      applicantId: formData.applicantId,
      photoAttachment: formData.photoAttachment || "",
      password: formData.password,
      lastName: formData.lastName,
      firstName: formData.firstName,
      assignedTo: formData.assignedToRoleId || 0,
      assignedEmployee: formData.assignedEmployeeId || 0,
    };

    setLoading(true);
    try {
      const response = await fetch(URLs.hrmsPostEmployeeDetailsUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Registration failed: ${response.status}`);
      }

      setAlert("Employee registered successfully", "success");
      resetForm();
      setActiveTab(0);
      fetchEmployees();
    } catch (error) {
      console.error("Registration error:", error);
      setAlert(`Registration failed: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedApplicant(null);
    setSelectedAssignedRole(null);
    setSelectedAssignedEmployee(null);
    setAssignedRoles([]);
    setAssignedEmployees([]);
    setFormData({
      applicantId: 0,
      companyId: user.dsaId,
      firstName: "",
      lastName: "",
      dateOfJoining: "",
      dateOfBirth: "",
      doorNo: "",
      streetName: "",
      cityName: "",
      districtName: "",
      stateName: "",
      zipCode: "",
      phoneNumber: "",
      alternatePhoneNumber: "",
      emailId: "",
      reference: "",
      role: null,
      roleId: 0,
      ctcPerYear: 0,
      additionalComments: "",
      resumeAttachment: "",
      basic: 0,
      password: "",
      applicationAccess: "No",
      photo: null,
      photoUrl: "",
      photoAttachmentPresignedUrl: "",
      photoAttachment: "",
      assignedToRoleId: null,
      assignedToRoleName: "",
      assignedEmployeeId: null,
      assignedEmployeeName: "",
    });
  };

  const handleDeleteEmployee = async (employeeId) => {
    if (!window.confirm("Are you sure you want to delete this employee?")) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${URLs.hrmsGetEmployeeDetailsUrl}/${employeeId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Delete failed: ${response.status}`);
      }

      setAlert("Employee deleted successfully", "success");
      fetchEmployees();
    } catch (error) {
      setAlert(`Failed to delete employee: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleViewPhoto = async (employee) => {
    const fileName = employee.photoAttachment;
    if (!fileName) {
      setAlert("No photo available to view", "error");
      return;
    }

    setLoading(true);
    try {
      const apiUrl = `https://s3service.sconexsoft.com/s3bucket/presigned-url?fileName=${encodeURIComponent(
        fileName
      )}&bucketName=sconexsofthrms&operation=download`;

      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      const viewUrl = response.data;
      if (!viewUrl || typeof viewUrl !== "string" || !viewUrl.startsWith("https://")) {
        throw new Error("Invalid pre-signed URL for viewing");
      }

      setSelectedPhoto(viewUrl);
      setPhotoDialogOpen(true);
      setAlert("Photo loaded successfully", "success");
    } catch (err) {
      console.error("Error fetching photo URL:", err);
      setAlert(`Failed to load photo: ${err.message || "Unknown error"}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPhoto = async () => {
    if (!selectedPhoto) return;
    setDownloading(true);
    try {
      const fileName = formData.photoAttachment || getFileNameFromUrl(selectedPhoto);
      const apiUrl = `https://s3service.sconexsoft.com/s3bucket/presigned-url?fileName=${encodeURIComponent(
        fileName
      )}&bucketName=sconexsofthrms&operation=download`;

      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      const downloadUrl = response.data;
      if (!downloadUrl || typeof downloadUrl !== "string") throw new Error("Invalid download URL");

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setAlert("Failed to download photo", "error");
    } finally {
      setDownloading(false);
    }
  };

  const getFileNameFromUrl = (url) => {
    try {
      return decodeURIComponent(url.split("/").pop().split("?")[0]);
    } catch {
      return "photo";
    }
  };

  const columns = [
    {
      headerName: "Employee Name",
      field: "employeeName",
      valueGetter: (params) =>
        `${params.data.firstName}${params.data.lastName ? " " + params.data.lastName : ""}`,
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: "Employee Code",
      field: "employeeCode",
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: "Role",
      field: "role.roleName",
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: "Email ID",
      field: "emailId",
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: "Photo",
      field: "photoAttachmentPresignedUrl",
      sortable: false,
      filter: false,
      width: 100,
      cellRenderer: (params) => (
        <IconButton
          size="small"
          onClick={() => handleViewPhoto(params.data)}
          color="primary"
          disabled={!params.data.photoAttachment}
        >
          <VisibilityIcon />
        </IconButton>
      ),
    },
    {
      headerName: "Actions",
      field: "actions",
      sortable: false,
      filter: false,
      width: 120,
      cellRenderer: (params) => (
        <Box>
          <IconButton size="small" onClick={() => handleEditEmployee(params.data)} color="primary">
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDeleteEmployee(params.data.employeeId)}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <DashboardLayout>
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", mt: 0 }}>
        <ResponsiveTabs
          tabs={tabs}
          activeTab={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          labelColor="secondary"
          bgColor="#e0f7fa"
        />

        <StyledSnackbar
          open={open}
          autoHideDuration={5000}
          onClose={handleClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <StyledAlert onClose={handleClose} severity={alertType}>
            {message}
          </StyledAlert>
        </StyledSnackbar>

        {activeTab === 0 ? (
          <Box sx={{ mt: 2 }}>
            <StyledAgGridContainer
              component={Paper}
              className="ag-theme-alpine"
              sx={{
                flexGrow: 1,
                backgroundColor: "transparent",
                height: "calc(100vh - 200px)",
                width: "100%",
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                <AgGridReact
                  rowData={employees}
                  columnDefs={columns}
                  pagination={true}
                  paginationPageSize={9}
                  defaultColDef={{
                    resizable: true,
                    filter: true,
                  }}
                  overlayNoRowsTemplate="<span>No employees found</span>"
                />
              )}
            </StyledAgGridContainer>
          </Box>
        ) : (
          <Box sx={{ mt: 2, width: "100%" }}>
            <Paper
              sx={{
                display: "flex",
                backgroundColor: "#ffffff",
                minHeight: "500px",
                borderRadius: "15px",
                overflow: "auto",
                width: "100%",
                p: 3,
              }}
            >
              <Grid container spacing={3}>
                {!isEditMode && (
                  <Grid item xs={12}>
                    <Autocomplete
                      options={applicants}
                      getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                      value={selectedApplicant}
                      onChange={handleApplicantChange}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Applicant"
                          variant="outlined"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={3}>
                  <TextField
                    label="First Name"
                    value={formData.firstName}
                    onChange={handleInputChange("firstName")}
                    fullWidth
                    disabled={!isEditMode}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Last Name"
                    value={formData.lastName}
                    onChange={handleInputChange("lastName")}
                    fullWidth
                    disabled={!isEditMode}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Phone Number"
                    value={formData.phoneNumber}
                    onChange={handleInputChange("phoneNumber")}
                    fullWidth
                    disabled={!isEditMode}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Alternative Phone"
                    value={formData.alternatePhoneNumber}
                    onChange={handleInputChange("alternatePhoneNumber")}
                    fullWidth
                    disabled={!isEditMode}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Email ID"
                    value={formData.emailId}
                    onChange={handleInputChange("emailId")}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Role"
                    value={formData.role?.roleName || formData.role || ""}
                    onChange={handleInputChange("role")}
                    fullWidth
                    disabled={true}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    options={assignedRoles}
                    getOptionLabel={(option) => option.roleName || ""}
                    value={selectedAssignedRole}
                    onChange={handleInputChange("assignedToRoleId")}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Assigned To (Role)"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.roleId === value?.roleId}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    options={assignedEmployees}
                    getOptionLabel={(option) => option.employeeName || ""}
                    value={selectedAssignedEmployee}
                    onChange={handleInputChange("assignedEmployeeId")}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Assigned Employee"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                    isOptionEqualToValue={(option, value) =>
                      option.employeeId === value?.employeeId
                    }
                    disabled={!selectedAssignedRole}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Door No"
                    value={formData.doorNo}
                    onChange={handleInputChange("doorNo")}
                    fullWidth
                    disabled={!isEditMode}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Street Name"
                    value={formData.streetName}
                    onChange={handleInputChange("streetName")}
                    fullWidth
                    disabled={!isEditMode}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="City"
                    value={formData.cityName}
                    onChange={handleInputChange("cityName")}
                    fullWidth
                    disabled={!isEditMode}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="District"
                    value={formData.districtName}
                    onChange={handleInputChange("districtName")}
                    fullWidth
                    disabled={!isEditMode}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="State"
                    value={formData.stateName}
                    onChange={handleInputChange("stateName")}
                    fullWidth
                    disabled={!isEditMode}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Zip Code"
                    value={formData.zipCode}
                    onChange={handleInputChange("zipCode")}
                    fullWidth
                    disabled={!isEditMode}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Reference"
                    value={formData.reference}
                    onChange={handleInputChange("reference")}
                    fullWidth
                    disabled={!isEditMode}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Date of Birth"
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange("dateOfBirth")}
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Date of Joining"
                    type="date"
                    value={formData.dateOfJoining}
                    onChange={handleInputChange("dateOfJoining")}
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    label="Application Access"
                    value={formData.applicationAccess}
                    onChange={handleInputChange("applicationAccess")}
                    fullWidth
                    SelectProps={{
                      style: { height: "56px" },
                      MenuProps: {
                        PaperProps: {
                          style: { maxHeight: 200 },
                        },
                      },
                    }}
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    type="password"
                    label="Password"
                    value={formData.password}
                    onChange={handleInputChange("password")}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexGrow: 1 }}>
                    <TextField
                      type="file"
                      label="Upload Photo"
                      fullWidth
                      onChange={handleFileChange}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        accept: "image/jpeg,image/png,image/gif",
                      }}
                      disabled={loading}
                    />
                    {formData.photoUrl && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <img
                          src={formData.photoUrl}
                          alt="Preview"
                          style={{
                            width: "100px",
                            height: "100px",
                            objectFit: "cover",
                            borderRadius: "4px",
                          }}
                        />
                        <StyledButton
                          variant="outlined"
                          color="error"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              photo: null,
                              photoUrl: "",
                              photoAttachmentPresignedUrl: "",
                              photoAttachment: "",
                            });
                          }}
                        >
                          Remove Photo
                        </StyledButton>
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    {isEditMode && (
                      <StyledButton
                        onClick={() => {
                          setIsEditMode(false);
                          setSelectedEmployeeId(null);
                          resetForm();
                        }}
                        variant="outlined"
                        color="secondary"
                      >
                        Cancel Edit
                      </StyledButton>
                    )}
                    <StyledButton
                      onClick={isEditMode ? handleUpdateEmployee : handleRegister}
                      disabled={loading || (!isEditMode && !selectedApplicant)}
                      variant="contained"
                    >
                      {loading
                        ? isEditMode
                          ? "Updating..."
                          : "Registering..."
                        : isEditMode
                        ? "Update Employee"
                        : "Register Employee"}
                    </StyledButton>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        )}

        <Dialog
          open={photoDialogOpen}
          onClose={() => setPhotoDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogContent>
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "400px",
                }}
              >
                <CircularProgress />
              </Box>
            ) : selectedPhoto ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  p: 2,
                }}
              >
                <img
                  src={selectedPhoto}
                  alt="Employee"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "400px",
                    objectFit: "contain",
                  }}
                />
              </Box>
            ) : (
              <Typography variant="body1" align="center">
                No photo available
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <StyledButton onClick={handleDownloadPhoto} disabled={downloading || loading}>
              {downloading ? "Downloading..." : "Download"}
            </StyledButton>
            <StyledButton onClick={() => setPhotoDialogOpen(false)}>Close</StyledButton>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  );
}
